import { Box } from "@mui/material";
import {
  type ChecklistItemDto,
  OrbitLChecklistIssue,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const MISSING_FIELD_RESOURCES =
  Resources.Components.Merchant.OrbitL.Checklist.MissingFields;

// The backend sends codes so this side owns the wording; a missing entry would be a new code, and
// the union type makes that a compile error rather than a blank bullet.
const MISSING_FIELD_LABEL_TRANSLATION_KEYS: Record<
  OrbitLChecklistIssue,
  string
> = {
  [OrbitLChecklistIssue.MissingExecutive]:
    MISSING_FIELD_RESOURCES.MissingExecutive,
  [OrbitLChecklistIssue.PrimaryBankAccount]:
    MISSING_FIELD_RESOURCES.PrimaryBankAccount,
  [OrbitLChecklistIssue.BankVerificationAudit]:
    MISSING_FIELD_RESOURCES.BankVerificationAudit,
  [OrbitLChecklistIssue.BirthDate]: MISSING_FIELD_RESOURCES.BirthDate,
  [OrbitLChecklistIssue.Email]: MISSING_FIELD_RESOURCES.Email,
  [OrbitLChecklistIssue.Phone]: MISSING_FIELD_RESOURCES.Phone,
  [OrbitLChecklistIssue.PermanentAddress]:
    MISSING_FIELD_RESOURCES.PermanentAddress,
  [OrbitLChecklistIssue.PermanentAddressStreet]:
    MISSING_FIELD_RESOURCES.PermanentAddressStreet,
  [OrbitLChecklistIssue.PermanentAddressMunicipality]:
    MISSING_FIELD_RESOURCES.PermanentAddressMunicipality,
  [OrbitLChecklistIssue.PermanentAddressPostalCode]:
    MISSING_FIELD_RESOURCES.PermanentAddressPostalCode,
  [OrbitLChecklistIssue.Documents]: MISSING_FIELD_RESOURCES.Documents,
  [OrbitLChecklistIssue.FirstDocumentNumber]:
    MISSING_FIELD_RESOURCES.FirstDocumentNumber,
  [OrbitLChecklistIssue.FirstDocumentValidity]:
    MISSING_FIELD_RESOURCES.FirstDocumentValidity,
  [OrbitLChecklistIssue.FirstDocumentPhotos]:
    MISSING_FIELD_RESOURCES.FirstDocumentPhotos,
  [OrbitLChecklistIssue.SecondDocumentNumber]:
    MISSING_FIELD_RESOURCES.SecondDocumentNumber,
  [OrbitLChecklistIssue.SecondDocumentValidity]:
    MISSING_FIELD_RESOURCES.SecondDocumentValidity,
  [OrbitLChecklistIssue.SecondDocumentPhotos]:
    MISSING_FIELD_RESOURCES.SecondDocumentPhotos,
};

type Props = {
  item: ChecklistItemDto;
};

/**
 * The concrete gaps behind a failed checklist item, named per person — a company with several
 * statutory representatives otherwise leaves the merchant guessing whose data is incomplete.
 */
export const ChecklistIssues = ({ item }: Props) => {
  const { t } = useResource();

  const itemIssues = item.issues ?? [];
  const personIssues = item.personIssues ?? [];

  if (itemIssues.length === 0 && personIssues.length === 0) {
    return null;
  }

  return (
    <Box component="ul" m={0} mt={0.5} pl={2.5}>
      {itemIssues.map((issue) => (
        <li key={issue}>{t(MISSING_FIELD_LABEL_TRANSLATION_KEYS[issue])}</li>
      ))}
      {personIssues.map(({ partyPublicID, displayName, missingFields }) => (
        // One interpolation rather than adjacent JSX children — the separator must not depend on
        // how the formatter wraps the lines.
        <li key={partyPublicID}>
          {`${displayName}: ${missingFields
            .map((field) => t(MISSING_FIELD_LABEL_TRANSLATION_KEYS[field]))
            .join(", ")}`}
        </li>
      ))}
    </Box>
  );
};
